// ═══════════════════════════════════════════════════════════════
// Handoff — AI Operational Briefing
// Claude-powered analysis of handoff notes, action items, positions
// Executive summary, hot issues, strengths/gaps, priority actions
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Paper, Text, Button, Group, Stack, Badge, SimpleGrid,
  Loader, Center, ThemeIcon,
} from '../gab/GabComponents';
import {
  IconSparkles, IconCheck, IconAlertTriangle, IconAlertCircle,
  IconCopy, IconClock, IconArrowsExchange,
} from '@tabler/icons-react';
import type { Incident, HandoffNote, ActionItem, Position } from '../../types';
import { callClaude } from '../../lib/api-switch';
import { primary, secondary, semantic } from '../../lib/colors';

// ── Types ────────────────────────────────────────────────────

interface HotIssue {
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  source: string;
}

interface PriorityAction {
  priority: number;
  action: string;
  rationale: string;
  urgency: 'immediate' | 'high' | 'normal';
  assignedTo: string;
}

interface BriefingResult {
  executiveSummary: string;
  operationalStatus: string;
  hotIssues: HotIssue[];
  strengths: string[];
  gaps: string[];
  priorityActions: PriorityAction[];
  positionCoverage: string;
  shiftReadiness: string;
  overallAssessment: {
    score: number;
    label: string;
    summary: string;
  };
  source: 'claude' | 'heuristic';
  generatedAt?: string;
  noteCount?: number;
  actionCount?: number;
}

interface Props {
  incident: Incident;
  notes: HandoffNote[];
  actionItems: ActionItem[];
  positions: Position[];
}

// ── Heuristic fallback ───────────────────────────────────────

function generateHeuristic(
  incident: Incident,
  notes: HandoffNote[],
  actions: ActionItem[],
  positions: Position[],
): BriefingResult {
  const submitted = notes.filter(n => n.status === 'submitted' || n.status === 'acknowledged');
  const pendingActions = actions.filter(a => a.status !== 'completed');
  const overdueActions = pendingActions.filter(a => a.deadline && new Date(a.deadline) < new Date());
  const criticalActions = pendingActions.filter(a => a.priority === 'critical' || a.priority === 'high');
  const filledPositions = positions.filter(p => p.dayUserName || p.nightUserName).length;
  const totalPositions = positions.length;
  const ackRate = submitted.length > 0
    ? Math.round(notes.filter(n => n.status === 'acknowledged').length / submitted.length * 100)
    : 0;

  let score = 7;
  if (overdueActions.length > 0) score -= Math.min(overdueActions.length, 3);
  if (criticalActions.length > 0) score -= 1;
  if (ackRate < 50) score -= 1;
  if (filledPositions < totalPositions * 0.7) score -= 1;
  if (submitted.length > 0) score += 1;
  score = Math.max(1, Math.min(10, score));

  const label = score >= 8 ? 'Strong' : score >= 6 ? 'Adequate' : score >= 4 ? 'Needs Attention' : 'Critical';

  // Strengths
  const strengths: string[] = [];
  if (submitted.length > 0) strengths.push(`${submitted.length} handoff note(s) submitted for current operational period`);
  if (ackRate >= 70) strengths.push(`Strong handoff acknowledgment rate (${ackRate}%)`);
  if (filledPositions >= totalPositions * 0.8 && totalPositions > 0) strengths.push(`${filledPositions} of ${totalPositions} positions staffed`);
  const completedActions = actions.filter(a => a.status === 'completed').length;
  if (completedActions > 0) strengths.push(`${completedActions} action item(s) completed`);
  if (strengths.length === 0) strengths.push('Operations are underway');

  // Gaps
  const gaps: string[] = [];
  if (overdueActions.length > 0) gaps.push(`${overdueActions.length} overdue action item(s) require immediate attention`);
  if (ackRate < 50 && submitted.length > 0) gaps.push(`Low handoff acknowledgment rate (${ackRate}%) — incoming shift may lack situational awareness`);
  if (totalPositions > 0 && filledPositions < totalPositions * 0.7) gaps.push(`Staffing gap — only ${filledPositions} of ${totalPositions} positions filled`);
  const noteDrafts = notes.filter(n => n.status === 'draft');
  if (noteDrafts.length > 0) gaps.push(`${noteDrafts.length} handoff draft(s) not yet submitted`);

  // Hot issues from overdue/critical actions
  const hotIssues: HotIssue[] = [
    ...overdueActions.slice(0, 3).map(a => ({
      title: a.title,
      description: a.description || `Overdue since ${a.deadline}. Assigned to ${a.assignedToName || 'unassigned'}.`,
      severity: 'critical' as const,
      source: 'Overdue Action Item',
    })),
    ...criticalActions.filter(a => !overdueActions.includes(a)).slice(0, 3).map(a => ({
      title: a.title,
      description: a.description || `Priority: ${a.priority}. Assigned to ${a.assignedToName || 'unassigned'}.`,
      severity: 'high' as const,
      source: 'High-Priority Action',
    })),
  ];

  // Priority actions
  const priorityActions: PriorityAction[] = [];
  if (overdueActions.length > 0) priorityActions.push({
    priority: 1, action: 'Resolve overdue action items',
    rationale: `${overdueActions.length} item(s) past deadline — blocking operational continuity.`,
    urgency: 'immediate', assignedTo: overdueActions[0].assignedToName || 'IC',
  });
  if (ackRate < 50 && submitted.length > 0) priorityActions.push({
    priority: priorityActions.length + 1, action: 'Follow up on unacknowledged handoffs',
    rationale: 'Incoming shift personnel must acknowledge handoffs to maintain continuity.',
    urgency: 'high', assignedTo: 'All Section Chiefs',
  });
  if (filledPositions < totalPositions && totalPositions > 0) priorityActions.push({
    priority: priorityActions.length + 1, action: 'Fill vacant ICS positions',
    rationale: `${totalPositions - filledPositions} position(s) unstaffed for current operational period.`,
    urgency: 'high', assignedTo: 'IC / Operations Chief',
  });
  pendingActions.filter(a => a.priority === 'critical').slice(0, 3).forEach(a => {
    priorityActions.push({
      priority: priorityActions.length + 1, action: a.title,
      rationale: a.description || 'Critical priority action item.',
      urgency: 'immediate', assignedTo: a.assignedToName || 'Unassigned',
    });
  });

  const execSummary = `The ${incident.name} operation has ${submitted.length} submitted handoff note(s) ` +
    `and ${pendingActions.length} pending action item(s). ` +
    (overdueActions.length > 0 ? `${overdueActions.length} action(s) are overdue and require immediate attention. ` : '') +
    `Position coverage is at ${totalPositions > 0 ? Math.round(filledPositions / totalPositions * 100) : 0}%. ` +
    `Handoff acknowledgment rate is ${ackRate}%. ` +
    (label === 'Strong' ? 'Operational posture is solid.' : label === 'Adequate' ? 'Operations are adequate with areas for improvement.' : 'Several areas require immediate command attention.');

  return {
    executiveSummary: execSummary,
    operationalStatus: label,
    hotIssues,
    strengths,
    gaps,
    priorityActions,
    positionCoverage: `${filledPositions} of ${totalPositions} positions staffed`,
    shiftReadiness: ackRate >= 70 ? 'Ready' : ackRate >= 40 ? 'Partially Ready' : 'Not Ready',
    overallAssessment: { score, label, summary: execSummary },
    source: 'heuristic',
  };
}

// ── Component ────────────────────────────────────────────────

export default function AIBriefing({ incident, notes, actionItems, positions }: Props) {
  const [result, setResult] = useState<BriefingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setResultWithMeta = (r: BriefingResult) => {
    r.generatedAt = new Date().toISOString();
    r.noteCount = notes.filter(n => n.status !== 'draft').length;
    r.actionCount = actionItems.filter(a => a.status === 'pending').length;
    setResult(r);
  };

  const runAnalysis = async (useAI: boolean) => {
    if (!useAI || notes.length === 0) {
      setResultWithMeta(generateHeuristic(incident, notes, actionItems, positions));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const submitted = notes.filter(n => n.status !== 'draft');
      const notesText = submitted.map(n => {
        const sects = n.sections.map(s => {
          const sec = s as unknown as Record<string, string>;
          return `    ${sec.heading || sec.title || 'Note'}: ${sec.body || sec.content || ''}`;
        }).join('\n');
        return `  [${n.positionName}] ${n.fromShift} → ${n.toShift} (${n.shiftDate})\n  Status: ${n.status} | Sender: ${n.senderName}\n${sects}`;
      }).join('\n\n');

      const pendingActions = actionItems.filter(a => a.status !== 'completed');
      const actionsText = pendingActions.map(a =>
        `  - [${a.priority.toUpperCase()}] ${a.title}: ${a.description} (assigned: ${a.assignedToName || 'unassigned'}, deadline: ${a.deadline || 'none'})`
      ).join('\n');

      const posText = positions.map(p =>
        `  ${p.positionName} (${p.shortName}): Day=${p.dayUserName || '⚠ VACANT'}, Night=${p.nightUserName || '⚠ VACANT'}`
      ).join('\n');

      const prompt = `You are a senior emergency management operations officer. Analyze these handoff documents, action items, and staffing positions to provide a comprehensive operational briefing for the Incident Commander.

INCIDENT: ${incident.name}
Type: ${incident.incidentType || 'Unknown'}
Status: ${incident.status}
Location: ${incident.location || 'Unknown'}
Description: ${incident.description || 'N/A'}

HANDOFF NOTES (${submitted.length} submitted):
${notesText || '(none)'}

PENDING ACTION ITEMS (${pendingActions.length}):
${actionsText || '(none)'}

ICS POSITIONS (${positions.length}):
${posText || '(none)'}

Provide a thorough operational assessment. Respond ONLY with a valid JSON object (no markdown, no backticks):
{
  "executiveSummary": "3-4 paragraph narrative covering current operational status, key activities, emerging issues, and overall posture. Write in professional EM briefing style.",
  "operationalStatus": "one of: Strong, Adequate, Needs Attention, Critical",
  "hotIssues": [
    {"title": "issue title", "description": "detailed description and impact", "severity": "critical|high|medium|low", "source": "where this was identified"}
  ],
  "strengths": ["strength 1", "strength 2", "...up to 5"],
  "gaps": ["gap or weakness 1", "gap 2", "...up to 5"],
  "priorityActions": [
    {"priority": 1, "action": "specific action needed", "rationale": "why this matters now", "urgency": "immediate|high|normal", "assignedTo": "suggested assignee"}
  ],
  "positionCoverage": "summary of staffing status",
  "shiftReadiness": "Ready|Partially Ready|Not Ready",
  "overallAssessment": {
    "score": 7,
    "label": "Strong|Adequate|Needs Attention|Critical",
    "summary": "one-sentence overall assessment"
  }
}`;

      const raw = await callClaude(
        [{ role: 'user', content: prompt }],
        {
          system: 'You are a senior emergency management operations officer. Provide thorough, actionable operational briefings. Return only valid JSON.',
          maxTokens: 4096,
        }
      );

      let cleaned = raw.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7);
      else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3);
      if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3);
      cleaned = cleaned.trim();

      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid response format');
      const parsed = JSON.parse(jsonMatch[0]) as BriefingResult;
      parsed.source = 'claude';
      setResultWithMeta(parsed);
    } catch (err) {
      console.error('Claude briefing failed, using heuristic:', err);
      setResultWithMeta(generateHeuristic(incident, notes, actionItems, positions));
      setError('Claude analysis unavailable — showing heuristic assessment.');
    } finally {
      setLoading(false);
    }
  };

  const sevColor = (s: string) =>
    s === 'critical' ? 'var(--color-error)' : s === 'high' ? 'var(--color-error)' : s === 'medium' ? 'var(--color-warning)' : secondary[700];

  const urgColor = (u: string) =>
    u === 'immediate' ? 'red' : u === 'high' ? 'orange' : 'gray';

  const scoreColor = result
    ? result.overallAssessment.score >= 8 ? 'var(--color-success)'
      : result.overallAssessment.score >= 6 ? 'var(--color-info)'
      : result.overallAssessment.score >= 4 ? 'var(--color-warning)'
      : semantic.error
    : secondary[700];

  const copyReport = () => {
    if (!result) return;
    const text = [
      'HANDOFF — AI OPERATIONAL BRIEFING',
      '====================================',
      `Incident: ${incident.name}`,
      `Type: ${incident.incidentType || 'N/A'} | Status: ${incident.status}`,
      `Source: ${result.source === 'claude' ? 'Claude AI Analysis' : 'Heuristic Analysis'}`,
      `Score: ${result.overallAssessment.score}/10 (${result.overallAssessment.label})`,
      '',
      'EXECUTIVE SUMMARY',
      '─────────────────',
      result.executiveSummary,
      '',
      'HOT ISSUES',
      '──────────',
      ...result.hotIssues.map(h => `  [${h.severity.toUpperCase()}] ${h.title}\n    ${h.description}\n    Source: ${h.source}`),
      ...(result.hotIssues.length === 0 ? ['  No hot issues identified.'] : []),
      '',
      'STRENGTHS',
      '─────────',
      ...result.strengths.map(s => `  + ${s}`),
      '',
      'GAPS & WEAKNESSES',
      '─────────────────',
      ...result.gaps.map(g => `  - ${g}`),
      ...(result.gaps.length === 0 ? ['  No significant gaps identified.'] : []),
      '',
      'PRIORITY ACTIONS',
      '────────────────',
      ...result.priorityActions.map(a => `  ${a.priority}. [${a.urgency.toUpperCase()}] ${a.action}\n     ${a.rationale}\n     Assigned: ${a.assignedTo}`),
      '',
      `Position Coverage: ${result.positionCoverage}`,
      `Shift Readiness: ${result.shiftReadiness}`,
    ].join('\n');
    navigator.clipboard.writeText(text);
  };

  if (notes.length === 0 && actionItems.length === 0) {
    return (
      <Paper p="xl" radius="sm" withBorder>
        <Center><Text c="dimmed">No handoff data to analyze. Submit handoff notes first.</Text></Center>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Group gap="xs">
          <Button
            leftSection={<IconSparkles size={14} />}
            onClick={() => runAnalysis(true)}
            loading={loading}
            size="xs"
          >
            {result?.source === 'claude' ? 'Regenerate Briefing' : 'Generate AI Briefing'}
          </Button>
          {!result && (
            <Button
              variant="light"
              size="xs"
              onClick={() => runAnalysis(false)}
            >
              Quick Assessment
            </Button>
          )}
          {result && (
            <>
              <Badge size="sm" variant="light" color={result.source === 'claude' ? 'blurple' : 'gray'}>
                {result.source === 'claude' ? 'Powered by Claude' : 'Heuristic'}
              </Badge>
              <Text size="xs" c="dimmed">
                {result.noteCount ?? 0} notes, {result.actionCount ?? 0} actions
                {result.generatedAt && ` · ${new Date(result.generatedAt).toLocaleTimeString()}`}
              </Text>
            </>
          )}
        </Group>
        {result && (
          <Button variant="subtle" size="xs" leftSection={<IconCopy size={14} />} onClick={copyReport}>
            Copy Report
          </Button>
        )}
      </Group>

      {error && (
        <Paper p="xs" radius="sm" bg="var(--warning-light)" style={{ border: '1px solid var(--color-warning)' }}>
          <Text size="xs" c="var(--warning)">{error}</Text>
        </Paper>
      )}

      {loading && (
        <Paper p="xl" radius="sm" withBorder>
          <Center>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Claude is analyzing handoff data...</Text>
              <Text size="xs" c="dimmed">Reviewing {notes.length} notes, {actionItems.length} action items, {positions.length} positions</Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {result && !loading && (
        <>
          {/* Overall Assessment Header */}
          <Paper p="md" radius="sm" withBorder>
            <Group gap="lg">
              <div style={{
                width: 72, height: 72, borderRadius: 4,
                background: scoreColor + '15', border: `2px solid ${scoreColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <Text fw={700} fz={28} style={{ color: scoreColor, lineHeight: 1 }}>{result.overallAssessment.score}</Text>
                <Text fz={10} c="dimmed">/10</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb="xs">
                  <Badge size="sm" style={{ background: scoreColor + '15', color: scoreColor }}>
                    {result.overallAssessment.label}
                  </Badge>
                  <Badge size="xs" variant="light" color="gray">{result.positionCoverage}</Badge>
                  <Badge size="xs" variant="light"
                    color={result.shiftReadiness === 'Ready' ? 'green' : result.shiftReadiness === 'Partially Ready' ? 'yellow' : 'red'}>
                    Shift: {result.shiftReadiness}
                  </Badge>
                </Group>
                <Text size="sm">{result.overallAssessment.summary}</Text>
                <SimpleGrid cols={4} mt="xs">
                  <div>
                    <Text fz={10} c="dimmed">Notes</Text>
                    <Text fw={600} size="sm">{notes.length}</Text>
                  </div>
                  <div>
                    <Text fz={10} c="dimmed">Actions</Text>
                    <Text fw={600} size="sm">{actionItems.filter(a => a.status !== 'completed').length} pending</Text>
                  </div>
                  <div>
                    <Text fz={10} c="dimmed">Hot Issues</Text>
                    <Text fw={600} size="sm" c={result.hotIssues.length > 0 ? 'var(--color-error)' : semantic.success}>
                      {result.hotIssues.length}
                    </Text>
                  </div>
                  <div>
                    <Text fz={10} c="dimmed">Positions</Text>
                    <Text fw={600} size="sm">{positions.length}</Text>
                  </div>
                </SimpleGrid>
              </div>
            </Group>
          </Paper>

          {/* Executive Summary */}
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="xs">Executive Summary</Text>
            <Text size="sm" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {result.executiveSummary}
            </Text>
          </Paper>

          {/* Hot Issues */}
          {result.hotIssues.length > 0 && (
            <Paper p="md" radius="sm" withBorder style={{ borderColor: 'var(--color-error)30' }}>
              <Group gap="xs" mb="sm">
                <IconAlertCircle size={16} color={semantic.error} />
                <Text fw={600} size="sm" c={semantic.error}>Hot Issues ({result.hotIssues.length})</Text>
              </Group>
              <Stack gap="sm">
                {result.hotIssues.map((h, i) => (
                  <Paper key={i} p="sm" radius="sm" bg="var(--bg-secondary)" style={{ border: `1px solid ${sevColor(h.severity)}20` }}>
                    <Group gap="xs" mb={4}>
                      <ThemeIcon size="xs" variant="light" radius="xl"
                        color={h.severity === 'critical' ? 'red' : h.severity === 'high' ? 'orange' : 'yellow'}>
                        <IconAlertTriangle size={10} />
                      </ThemeIcon>
                      <Text size="sm" fw={600}>{h.title}</Text>
                      <Badge size="xs" style={{ background: sevColor(h.severity) + '15', color: sevColor(h.severity) }}>
                        {h.severity}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" pl={24}>{h.description}</Text>
                    <Text size="xs" c="dimmed" pl={24} mt={2} fs="italic">Source: {h.source}</Text>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Strengths & Gaps */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Strengths</Text>
              <Stack gap="xs">
                {result.strengths.map((s, i) => (
                  <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                    <ThemeIcon size="xs" color="green" variant="light" radius="xl" mt={3}>
                      <IconCheck size={10} />
                    </ThemeIcon>
                    <Text size="sm">{s}</Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Gaps & Weaknesses</Text>
              {result.gaps.length === 0 ? (
                <Text size="sm" c="dimmed">No significant gaps identified.</Text>
              ) : (
                <Stack gap="xs">
                  {result.gaps.map((g, i) => (
                    <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                      <ThemeIcon size="xs" color="red" variant="light" radius="xl" mt={3}>
                        <IconAlertTriangle size={10} />
                      </ThemeIcon>
                      <Text size="sm">{g}</Text>
                    </Group>
                  ))}
                </Stack>
              )}
            </Paper>
          </SimpleGrid>

          {/* Priority Actions */}
          <Paper p="md" radius="sm" withBorder>
            <Group gap="xs" mb="sm">
              <IconClock size={16} color={primary[700]} />
              <Text fw={600} size="sm">Priority Actions</Text>
            </Group>
            <Stack gap="sm">
              {result.priorityActions.map((a) => (
                <Group key={a.priority} gap="sm" wrap="nowrap" align="flex-start">
                  <Text fw={700} fz={18} c="blurple" style={{ minWidth: 28, textAlign: 'center', lineHeight: 1 }}>
                    {a.priority}
                  </Text>
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={600}>{a.action}</Text>
                      <Badge size="xs" color={urgColor(a.urgency)}>
                        {a.urgency === 'immediate' ? 'Immediate' : a.urgency === 'high' ? 'High' : 'Normal'}
                      </Badge>
                      {a.assignedTo && (
                        <Badge size="xs" variant="outline" color="gray">
                          <Group gap={4}><IconArrowsExchange size={10} />{a.assignedTo}</Group>
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">{a.rationale}</Text>
                  </div>
                </Group>
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
}
